/**
 * Mock Login Screen
 * 
 * Simple login interface for mock authentication mode.
 * Accepts any email/password and generates a mock JWT token.
 * 
 * USAGE: Only shown when REACT_APP_AUTH_MODE=mock
 * 
 * @module components/auth/MockLoginScreen
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Spinner } from '@sequelae/lore-core';
import './MockLoginScreen.css';

export const MockLoginScreen: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const [email, setEmail] = useState('test@example.com');
  const [password, setPassword] = useState('password');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await login(email, password);
      // Navigation handled by App.tsx based on auth state
    } catch (err) {
      console.error('[MockLoginScreen] Login failed:', err);
    }
  };

  return (
    <div className="mock-login-container">
      <div className="mock-login-card">
        <div className="mock-login-header">
          <h1>Lore Web Client</h1>
          <p className="mock-login-subtitle">Mock Authentication Mode</p>
        </div>

        <form onSubmit={handleSubmit} className="mock-login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter any email"
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter any password"
              disabled={isLoading}
              required
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="medium"
            fullWidth
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : 'Sign In'}
          </Button>
        </form>

        <div className="mock-login-footer">
          <p className="mock-login-note">
            ℹ️ Mock mode accepts any email/password combination
          </p>
        </div>
      </div>
    </div>
  );
};

